import {
  Alert as MuiAlert,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FileUpload from "react-mui-fileuploader";
import React, { forwardRef, useState } from "react";
import HttpService from "../services/HttpService";
import uploadImg from "./../images/upload.svg";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DataGrid } from "@mui/x-data-grid";

const FIELD_LABELS = {
  email: "Email",
  firstName: "First Name",
  middleName: "Middle Name",
  lastName: "Last Name",
  phone: "Phone",
  birthDate: "Birth Date",
  addressLine1: "Address Line 1",
  addressLine2: "Address Line 2",
  city: "City",
  state: "State",
  zipCode: "Zip Code",
  zip: "Zip Code",
  rate: "Rate",
  rateType: "Rate Type",
  mailingAddressLine1: "Mailing Address Line 1",
  mailingAddressLine2: "Mailing Address Line 2",
  mailingCity: "Mailing City",
  mailingState: "Mailing State",
  mailingZipCode: "Mailing Zip Code",
  idEmployee: "Employee ID",
  idLanguage: "Language",
  timeCardId: "Time Card ID",
};

/**
 * Two-step upload component for PAD Standalone pre-authorization files.
 *
 * Step 1 — Pick file + analyze:
 *   Sends the file to /analyze. If the backend is confident in the mapping,
 *   auto-proceeds to upload without showing the confirmation screen.
 *   Otherwise moves to step 2.
 *
 * Step 2 — Confirm mapping (only shown when confidence is low):
 *   Table of field → column assignments. User adjusts and confirms.
 *   Sends file + confirmed mapping + employerId to /upload-mapped.
 */
export default function PreAuthorizationUpload({ setOpen, employerId }) {
  const queryClient = useQueryClient();

  const [step, setStep] = useState("pick"); // "pick" | "confirm" | "result"
  const [selectedFile, setSelectedFile] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [confirmedMapping, setConfirmedMapping] = useState({});
  const [uploadResult, setUploadResult] = useState(null);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={10} ref={ref} variant="filled" {...props} />;
  });

  const handleFeedbackClose = (event, reason) => {
    if (reason === "clickaway") return;
    setFeedback({ open: false, type: "error", message: "" });
  };

  // ── Helper: read file as raw base64 ─────────────────────────────────────
  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(",")[1]); // strip data:...;base64,
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  // ── Upload mutation ──────────────────────────────────────────────────────

  const uploadMutation = useMutation(
    async ({ file, mapping, replaceExisting: replace }) => {
      const base64Content = await toBase64(file);
      return HttpService.getAxiosClient().post(
        window.API_URL + `/onboarding/preauthorized/upload-mapped`,
        {
          fileName: file.name,
          base64Content,
          employerId: employerId ?? "",
          mapping: JSON.stringify(mapping),
          replaceExisting: replace ?? false,
        }
      );
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries({ queryKey: ["bulk_preauthorization"] });
        setUploadResult(response?.data ?? null);
        setStep("result");
      },
      onError: (err) => {
        const msg = err?.response?.data ?? err.message;
        setFeedback({ open: true, type: "error", message: msg });
      },
    }
  );

  // ── Analyze mutation ─────────────────────────────────────────────────────

  const analyzeMutation = useMutation(
    async (file) => {
      const base64Content = await toBase64(file);
      return HttpService.getAxiosClient().post(
        window.API_URL + `/onboarding/preauthorized/analyze`,
        {
          fileName: file.name,
          base64Content,
          employerId: employerId ?? "",
        }
      );
    },
    {
      onSuccess: (response) => {
        const result = response.data;
        setAnalysisResult(result);

        // Build initial mapping state from proposal (-1 = not in file)
        const initialMapping = {};
        for (const field of result.allFields) {
          initialMapping[field] = result.proposedMapping[field] ?? -1;
        }
        setConfirmedMapping(initialMapping);

        if (result.confident) {
          // Auto-proceed: upload immediately without showing confirmation screen
          uploadMutation.mutate({
            file: selectedFile,
            mapping: result.proposedMapping,
            replaceExisting,
          });
        } else {
          setStep("confirm");
        }
      },
      onError: (err) => {
        const msg = err?.response?.data ?? err.message;
        setFeedback({ open: true, type: "error", message: msg });
      },
    }
  );

  const isLoading = analyzeMutation.isLoading || uploadMutation.isLoading;

  const handleAnalyze = () => {
    if (selectedFile) analyzeMutation.mutate(selectedFile);
  };

  const handleConfirmUpload = () => {
    const requiredFields = analysisResult?.requiredFields ?? [];
    const missing = requiredFields.filter(
      (f) => (confirmedMapping[f] ?? -1) < 0
    );
    if (missing.length > 0) {
      setFeedback({
        open: true,
        type: "error",
        message: `Please assign columns for required fields: ${missing
          .map((f) => FIELD_LABELS[f] ?? f)
          .join(", ")}`,
      });
      return;
    }
    const finalMapping = {};
    for (const [field, idx] of Object.entries(confirmedMapping)) {
      if (idx >= 0) finalMapping[field] = idx;
    }
    uploadMutation.mutate({
      file: selectedFile,
      mapping: finalMapping,
      replaceExisting,
    });
  };

  // ── Step 1: Pick file ────────────────────────────────────────────────────

  if (step === "pick") {
    return (
      <Box component="form" noValidate autoComplete="off">
        <Grid
          style={{ marginLeft: "20px", marginRight: "10px", marginTop: 0 }}
          container
          spacing={1.5}
        >
          <Container maxWidth="xlg">
            <Grid container spacing={1.5} style={{ maxWidth: "80%" }}>
              <Grid item xs={12} md={4}>
                <br />
                <br />
                <img src={uploadImg} alt="upload" width="50%" loading="lazy" />
              </Grid>
              <Grid item xs={12} md={8}>
                <h1>Upload File</h1>
                <FileUpload
                  multiFile={false}
                  onFilesChange={(files) =>
                    setSelectedFile(files.length === 1 ? files[0] : null)
                  }
                  title="Please upload the CSV or XLSX file"
                  showPlaceholderImage={false}
                  allowedExtensions={[
                    "csv",
                    "xlsx",
                    "vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                  ]}
                  maxUploadFiles={1}
                  header="> DROP HERE <"
                  BannerProps={{
                    elevation: 0,
                    variant: "outlined",
                    sx: {
                      "& .MuiGrid-item": {
                        color: "#0d7f81",
                        backgroundColor: "aliceblue",
                        borderColor: "#0d7f81",
                      },
                      "& .MuiButtonBase-root": {
                        color: "#0d7f81",
                        backgroundColor: "aliceblue",
                        borderColor: "#0d7f81",
                      },
                    },
                  }}
                  ContainerProps={{
                    elevation: 0,
                    variant: "outlined",
                    sx: {
                      p: 1,
                      "& .MuiGrid-item": {
                        color: "#1976d2",
                        backgroundColor: "aliceblue",
                        borderColor: "#1976d2",
                      },
                      "& .MuiGrid-root": {
                        color: "#1976d2",
                        backgroundColor: "aliceblue",
                        borderColor: "#1976d2",
                      },
                    },
                  }}
                  onContextReady={() => {}}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={replaceExisting}
                      onChange={(e) => setReplaceExisting(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Replace existing records"
                  sx={{ display: "block", ml: 0.5, mb: 0.5 }}
                />
                <Button
                  disabled={!selectedFile || isLoading}
                  style={{ margin: "10px" }}
                  onClick={handleAnalyze}
                  variant="contained"
                >
                  {isLoading ? (
                    <CircularProgress
                      size="1.5rem"
                      style={{ paddingRight: "1px", color: "white" }}
                    />
                  ) : (
                    <CloudUploadIcon style={{ paddingRight: "5px" }} />
                  )}
                  Analyze &amp; Upload
                </Button>
              </Grid>
            </Grid>

            <Snackbar
              open={feedback.open}
              autoHideDuration={feedback.type === "warning" ? null : 6000}
              onClose={feedback.onClose ?? handleFeedbackClose}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
              <Alert
                onClose={feedback.onClose ?? handleFeedbackClose}
                severity={feedback.type}
                sx={{ width: "100%" }}
              >
                {feedback.message}
              </Alert>
            </Snackbar>
          </Container>
        </Grid>
      </Box>
    );
  }

  // ── Step 3: Upload result ────────────────────────────────────────────────

  if (step === "result") {
    const rows = (uploadResult?.rows ?? []).map((r, idx) => ({
      id: idx,
      rowNumber: r.rowNumber,
      status: r.status,
      note:
        r.status === "updated"
          ? `Changed: ${(r.changedFields ?? []).map((f) => FIELD_LABELS[f] ?? f).join(", ") || "—"}`
          : r.status === "duplicate"
            ? "No changes — already exists"
            : (r.missingFields ?? [])
                .map((f) => FIELD_LABELS[f] ?? f)
                .join(", "),
      recEmail: r.recordValues?.email ?? "",
      recFirstName: r.recordValues?.firstName ?? "",
      recLastName: r.recordValues?.lastName ?? "",
      recPhone: r.recordValues?.phone ?? "",
      recIdEmployee: r.recordValues?.idEmployee ?? "",
      recRate: r.recordValues?.rate ?? "",
      recRateType: r.recordValues?.rateType ?? "",
    }));

    const resultColumns = [
      { field: "rowNumber", headerName: "Row #", width: 70 },
      {
        field: "status",
        headerName: "Status",
        width: 130,
        renderCell: (params) =>
          params.value === "imported" ? (
            <Chip
              icon={<CheckCircleIcon />}
              label="Imported"
              size="small"
              color="success"
              variant="outlined"
            />
          ) : params.value === "updated" ? (
            <Chip
              icon={<WarningAmberIcon />}
              label="Updated"
              size="small"
              color="info"
              variant="outlined"
            />
          ) : params.value === "duplicate" ? (
            <Chip
              label="Duplicate"
              size="small"
              color="default"
              variant="outlined"
            />
          ) : (
            <Chip
              icon={<WarningAmberIcon />}
              label="Skipped"
              size="small"
              color="warning"
              variant="outlined"
            />
          ),
      },
      { field: "recEmail", headerName: "Email", width: 180 },
      { field: "recFirstName", headerName: "First Name", width: 120 },
      { field: "recLastName", headerName: "Last Name", width: 120 },
      { field: "recPhone", headerName: "Phone", width: 120 },
      { field: "recIdEmployee", headerName: "Emp. ID", width: 100 },
      { field: "recRate", headerName: "Rate", width: 100 },
      { field: "recRateType", headerName: "Rate Type", width: 110 },
      {
        field: "note",
        headerName: "Notes / Missing Fields",
        flex: 1,
        renderCell: (params) => (
          <Typography variant="body2" color="text.secondary">
            {params.value}
          </Typography>
        ),
      },
    ];

    const importedCount = uploadResult?.importedCount ?? 0;
    const updatedCount = uploadResult?.updatedCount ?? 0;
    const duplicateCount = uploadResult?.duplicateCount ?? 0;
    const skippedCount = uploadResult?.skippedCount ?? 0;
    const hasSkipped = skippedCount > 0;
    const hasUpdated = updatedCount > 0;
    const hasDuplicate = duplicateCount > 0;
    const hasIssues = hasSkipped || hasUpdated || hasDuplicate;

    const skippedSummary = uploadResult?.skippedSummary ?? {};
    const skippedSummaryEntries = Object.entries(skippedSummary);

    const exportIssues = () => {
      const issueRows = (uploadResult?.rows ?? []).filter(
        (r) => r.status !== "imported"
      );
      const header = [
        "Row #",
        "Status",
        "Email",
        "First Name",
        "Last Name",
        "Phone",
        "Employee ID",
        "Rate",
        "Rate Type",
        "Notes / Reason",
      ];
      const csvLines = [header.join(",")];
      issueRows.forEach((r) => {
        const statusLabel =
          r.status === "updated"
            ? "Updated"
            : r.status === "duplicate"
              ? "Duplicate"
              : "Skipped";
        const reason =
          r.status === "updated"
            ? `Changed: ${(r.changedFields ?? []).map((f) => FIELD_LABELS[f] ?? f).join("; ") || "—"}`
            : r.status === "duplicate"
              ? "No changes — already exists"
              : (r.missingFields ?? [])
                  .map((f) => FIELD_LABELS[f] ?? f)
                  .join("; ");
        const cols = [
          r.rowNumber,
          statusLabel,
          r.recordValues?.email ?? "",
          r.recordValues?.firstName ?? "",
          r.recordValues?.lastName ?? "",
          r.recordValues?.phone ?? "",
          r.recordValues?.idEmployee ?? "",
          r.recordValues?.rate ?? "",
          r.recordValues?.rateType ?? "",
          reason,
        ].map((v) => `"${String(v).replace(/"/g, '""')}"`);
        csvLines.push(cols.join(","));
      });
      const blob = new Blob([csvLines.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "upload_issues.csv";
      link.click();
      URL.revokeObjectURL(url);
    };

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Upload Complete
        </Typography>

        <Box display="flex" gap={2} mb={2} flexWrap="wrap">
          <Chip
            icon={<CheckCircleIcon />}
            label={`${importedCount} row${importedCount !== 1 ? "s" : ""} imported`}
            color="success"
            variant="filled"
          />
          {hasUpdated && (
            <Chip
              icon={<WarningAmberIcon />}
              label={`${updatedCount} row${updatedCount !== 1 ? "s" : ""} updated`}
              color="info"
              variant="filled"
            />
          )}
          {hasDuplicate && (
            <Chip
              label={`${duplicateCount} duplicate${duplicateCount !== 1 ? "s" : ""} — no change`}
              color="default"
              variant="filled"
            />
          )}
          {hasSkipped && (
            <Chip
              icon={<WarningAmberIcon />}
              label={`${skippedCount} row${skippedCount !== 1 ? "s" : ""} skipped`}
              color="warning"
              variant="filled"
            />
          )}
        </Box>

        {hasSkipped && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Skipped rows are missing one or more required fields. Review the
            details, correct your CSV, and re-upload if needed.
          </Typography>
        )}
        {hasUpdated && (
          <Typography variant="body2" color="info.main" sx={{ mb: 1 }}>
            Updated rows had an existing record — their information was
            refreshed with the new data from this file.
          </Typography>
        )}
        {hasDuplicate && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Duplicate rows matched an existing record with identical data — no
            action was taken.
          </Typography>
        )}

        {skippedSummaryEntries.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Skip reasons summary
            </Typography>
            <Table size="small" sx={{ width: "auto" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Missing Field
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="right">
                    Rows Affected
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {skippedSummaryEntries.map(([field, count]) => (
                  <TableRow key={field}>
                    <TableCell>{FIELD_LABELS[field] ?? field}</TableCell>
                    <TableCell align="right">{count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        <Box
          sx={{
            height: Math.min(400, 56 + rows.length * 36 + 56),
            width: "100%",
          }}
        >
          <DataGrid
            rows={rows}
            columns={resultColumns}
            density="compact"
            disableColumnSelector
            disableDensitySelector
            getRowClassName={(params) =>
              params.row.status === "skipped"
                ? "row-skipped"
                : params.row.status === "updated"
                  ? "row-updated"
                  : params.row.status === "duplicate"
                    ? "row-duplicate"
                    : ""
            }
            sx={{
              "& .row-skipped": { backgroundColor: "#fff8e1" },
              "& .row-updated": { backgroundColor: "#e3f2fd" },
              "& .row-duplicate": { backgroundColor: "#f5f5f5" },
            }}
          />
        </Box>

        <Box display="flex" gap={2} mt={3} flexWrap="wrap">
          <Button variant="contained" onClick={() => setOpen(false)}>
            Close
          </Button>
          {hasIssues && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportIssues}
            >
              Export Issues
            </Button>
          )}
          {hasIssues && (
            <Button
              variant="outlined"
              onClick={() => {
                setStep("pick");
                setSelectedFile(null);
                setAnalysisResult(null);
                setUploadResult(null);
              }}
            >
              Upload Another File
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  // ── Step 2: Confirm mapping ──────────────────────────────────────────────

  const requiredSet = new Set(analysisResult?.requiredFields ?? []);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Confirm Column Mapping
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Review how columns in your file were matched to the expected fields.
        Adjust any mismatches before uploading.
      </Typography>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <strong>Field</strong>
            </TableCell>
            <TableCell>
              <strong>Mapped to Column</strong>
            </TableCell>
            <TableCell>
              <strong>Sample Value</strong>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(analysisResult?.allFields ?? []).map((field) => {
            const isRequired = requiredSet.has(field);
            const currentIdx = confirmedMapping[field] ?? -1;
            const isConflict = analysisResult.conflicts?.includes(field);
            const sampleValue =
              currentIdx >= 0
                ? (analysisResult.sampleRow?.[currentIdx] ?? "")
                : "";
            const hasError = isRequired && currentIdx < 0;

            return (
              <TableRow
                key={field}
                sx={hasError ? { backgroundColor: "#fff5f5" } : {}}
              >
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <span>{FIELD_LABELS[field] ?? field}</span>
                    {isRequired && (
                      <Chip
                        label="required"
                        size="small"
                        color="error"
                        variant="outlined"
                      />
                    )}
                    {isConflict && (
                      <Chip
                        label="conflict"
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <FormControl
                    size="small"
                    sx={{ minWidth: 220 }}
                    error={hasError}
                  >
                    <Select
                      value={currentIdx}
                      onChange={(e) =>
                        setConfirmedMapping((prev) => ({
                          ...prev,
                          [field]: Number(e.target.value),
                        }))
                      }
                    >
                      <MenuItem value={-1}>
                        <em>-- not in file --</em>
                      </MenuItem>
                      {(analysisResult?.headers ?? []).map((header, idx) => (
                        <MenuItem key={idx} value={idx}>
                          {header}
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 1 }}
                          >
                            (col {idx})
                          </Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {sampleValue}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Box display="flex" gap={2} mt={3}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => {
            setStep("pick");
            setAnalysisResult(null);
          }}
          disabled={isLoading}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirmUpload}
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress size="1.5rem" style={{ color: "white" }} />
          ) : (
            "Confirm & Upload"
          )}
        </Button>
      </Box>

      <Snackbar
        open={feedback.open}
        autoHideDuration={feedback.type === "warning" ? null : 6000}
        onClose={feedback.onClose ?? handleFeedbackClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={feedback.onClose ?? handleFeedbackClose}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
